import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const MaskedLine = ({ children, delay, className = "", ...rest }) => (
  <div className="overflow-hidden" {...rest}>
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ type: "spring", damping: 22, stiffness: 90, delay }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

const Hero = ({ t }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });

  const orb1X = useTransform(sx, (v) => v * 60);
  const orb1Y = useTransform(sy, (v) => v * 40);
  const orb2X = useTransform(sx, (v) => v * -80);
  const orb2Y = useTransform(sy, (v) => v * -50);
  const orb3X = useTransform(sx, (v) => v * 30);
  const orb3Y = useTransform(sy, (v) => v * -30);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden px-6 md:px-12"
    >
      <motion.div
        aria-hidden="true"
        style={{ x: orb1X, y: orb1Y }}
        className="absolute top-[15%] left-[8%] w-[420px] h-[420px] rounded-full bg-[#D000FF] opacity-25 blur-[120px]"
      />
      <motion.div
        aria-hidden="true"
        style={{ x: orb2X, y: orb2Y }}
        className="absolute bottom-[10%] right-[5%] w-[520px] h-[520px] rounded-full bg-[#00F0FF] opacity-20 blur-[140px]"
      />
      <motion.div
        aria-hidden="true"
        style={{ x: orb3X, y: orb3Y }}
        className="absolute top-[45%] left-[55%] w-[240px] h-[240px] rounded-full bg-[#7B2FFF] opacity-20 blur-[100px]"
      />

      <div className="relative z-10 max-w-7xl mx-auto w-full pt-28">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="text-sm font-bold tracking-[0.25em] uppercase text-[#00F0FF] mb-8"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1
          className="font-display font-black uppercase tracking-tighter leading-[0.95] text-[13vw] sm:text-6xl md:text-7xl lg:text-8xl"
          data-testid="hero-title"
        >
          <span className="sr-only">{t.hero.lines.join(" ")}</span>
          <MaskedLine delay={0.5} aria-hidden="true">
            {t.hero.lines[0]}
          </MaskedLine>
          <MaskedLine delay={0.65} className="text-outline" aria-hidden="true">
            {t.hero.lines[1]}
          </MaskedLine>
          <MaskedLine delay={0.8} className="text-magic" aria-hidden="true">
            {t.hero.lines[2]}
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-white/70"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-12"
        >
          <a
            href="#giochi"
            data-testid="hero-cta-button"
            className="btn-magic inline-flex items-center gap-3 rounded-full px-8 py-4 font-display font-bold text-sm tracking-wide uppercase text-white"
          >
            {t.hero.cta}
            <ArrowDown className="w-4 h-4" strokeWidth={2} />
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
